import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { Home, RefreshCw, Check, AlertTriangle, FileText, Book } from 'lucide-react';
import { useContent } from '../../contexts/SupabaseContentContext';
import { migrateContentImages, migrateFeaturedImage } from '../../utils/contentImageMigration';

const ContentBatchUpdater: React.FC = () => {
  const { blogPosts, docPosts, updateBlogPost, updateDocPost, refreshData } = useContent();
  
  const [isProcessing, setIsProcessing] = useState(false);
  const [currentItem, setCurrentItem] = useState<string>('');
  const [processedCount, setProcessedCount] = useState(0);
  const [totalCount, setTotalCount] = useState(0);
  const [progress, setProgress] = useState(0);
  const [logs, setLogs] = useState<string[]>([]);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState(false);

  // Set page title
  useEffect(() => {
    document.title = 'Cập nhật hàng loạt - VMon Cloud';
    return () => {
      document.title = 'VMon Cloud - Dịch vụ VPS và Hosting chất lượng cao';
    };
  }, []);

  // Add log message
  const addLog = (message: string) => {
    setLogs(prev => [...prev, `${new Date().toLocaleTimeString()}: ${message}`]);
  };

  // Process all content
  const processAllContent = async () => {
    setIsProcessing(true);
    setError(null);
    setLogs([]);
    setProgress(0);
    setProcessedCount(0);
    setSuccess(false);
    
    const allItems = [...blogPosts, ...docPosts];
    setTotalCount(allItems.length);
    
    try {
      addLog(`Bắt đầu xử lý ${allItems.length} mục nội dung...`);
      
      // Process blog posts
      for (let i = 0; i < blogPosts.length; i++) {
        const post = blogPosts[i];
        setCurrentItem(post.title);
        
        try {
          addLog(`Đang xử lý bài viết: ${post.title}`);
          
          // Process content
          const { updatedContent, replacedUrls } = await migrateContentImages(
            post.content,
            (current, total) => {
              // This is for image progress within a single post
            }
          );
          
          // Process featured image if needed
          let updatedFeaturedImage = post.featured_image;
          if (post.featured_image && post.featured_image.includes('vmon.vn') && !post.featured_image.includes('images.vmon.vn')) {
            addLog(`Đang xử lý ảnh bìa: ${post.featured_image}`);
            updatedFeaturedImage = await migrateFeaturedImage(post.featured_image);
          }
          
          // Update post if changes were made
          if (updatedContent !== post.content || updatedFeaturedImage !== post.featured_image) {
            await updateBlogPost(post.id, {
              content: updatedContent,
              featured_image: updatedFeaturedImage
            });
            
            if (replacedUrls.length > 0) {
              addLog(`✅ Đã thay thế ${replacedUrls.length} ảnh trong bài viết "${post.title}"`);
            }
            
            if (updatedFeaturedImage !== post.featured_image) {
              addLog(`✅ Đã thay thế ảnh bìa trong bài viết "${post.title}"`);
            }
          } else {
            addLog(`ℹ️ Không có ảnh nào cần thay thế trong bài viết "${post.title}"`);
          }
        } catch (err) {
          const errorMessage = err instanceof Error ? err.message : String(err);
          addLog(`❌ Lỗi khi xử lý bài viết "${post.title}": ${errorMessage}`);
        }
        
        // Update progress
        setProcessedCount(i + 1);
        setProgress(((i + 1) / allItems.length) * 100);
      }
      
      // Process doc posts
      for (let i = 0; i < docPosts.length; i++) {
        const doc = docPosts[i];
        setCurrentItem(doc.title);
        
        try {
          addLog(`Đang xử lý tài liệu: ${doc.title}`);
          
          // Process content
          const { updatedContent, replacedUrls } = await migrateContentImages(
            doc.content,
            (current, total) => {
              // This is for image progress within a single doc
            }
          );
          
          // Update doc if changes were made
          if (updatedContent !== doc.content) {
            await updateDocPost(doc.id, {
              content: updatedContent
            });
            
            if (replacedUrls.length > 0) {
              addLog(`✅ Đã thay thế ${replacedUrls.length} ảnh trong tài liệu "${doc.title}"`);
            }
          } else {
            addLog(`ℹ️ Không có ảnh nào cần thay thế trong tài liệu "${doc.title}"`);
          }
        } catch (err) {
          const errorMessage = err instanceof Error ? err.message : String(err);
          addLog(`❌ Lỗi khi xử lý tài liệu "${doc.title}": ${errorMessage}`);
        }
        
        // Update progress
        setProcessedCount(blogPosts.length + i + 1);
        setProgress(((blogPosts.length + i + 1) / allItems.length) * 100);
      }
      
      // Refresh data
      await refreshData();
      
      setSuccess(true);
      addLog(`🎉 Hoàn thành! Đã xử lý ${allItems.length} mục nội dung.`);
    } catch (err) {
      const errorMessage = err instanceof Error ? err.message : String(err);
      setError(`Lỗi khi xử lý nội dung: ${errorMessage}`);
      addLog(`❌ Lỗi: ${errorMessage}`);
    } finally {
      setIsProcessing(false);
    }
  };

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Header */}
      <div className="bg-white shadow">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex justify-between items-center py-6">
            <div className="flex items-center">
              <Link
                to="/vmon-admin"
                className="mr-4 p-2 text-gray-400 hover:text-blue-600 hover:bg-blue-50 rounded-lg transition-colors"
                title="Quay về Dashboard"
              >
                <Home className="h-5 w-5" />
              </Link>
              <div>
                <h1 className="text-3xl font-bold text-gray-900">Cập nhật hàng loạt</h1>
                <p className="text-gray-600">Cập nhật tất cả ảnh từ vmon.vn sang Supabase Storage</p>
              </div>
            </div>
          </div>
        </div>
      </div>

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {/* Content Stats */}
        <div className="bg-white rounded-lg shadow p-6 mb-6">
          <h2 className="text-xl font-bold text-gray-900 mb-4">Thống kê nội dung</h2>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div className="bg-blue-50 p-4 rounded-lg">
              <div className="flex items-center">
                <FileText className="h-8 w-8 text-blue-600 mr-3" />
                <div>
                  <h3 className="font-semibold text-gray-900">Bài viết Blog</h3>
                  <p className="text-2xl font-bold text-blue-600">{blogPosts.length}</p>
                </div>
              </div>
            </div>
            
            <div className="bg-green-50 p-4 rounded-lg">
              <div className="flex items-center">
                <Book className="h-8 w-8 text-green-600 mr-3" />
                <div>
                  <h3 className="font-semibold text-gray-900">Tài liệu</h3>
                  <p className="text-2xl font-bold text-green-600">{docPosts.length}</p>
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Process Content */}
        <div className="bg-white rounded-lg shadow p-6 mb-6">
          <h2 className="text-xl font-bold text-gray-900 mb-4">Cập nhật ảnh</h2>
          
          {error && (
            <div className="bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded mb-4 flex items-start">
              <AlertTriangle className="h-5 w-5 text-red-500 mr-2 mt-0.5 flex-shrink-0" />
              <div>{error}</div>
            </div>
          )}
          
          {success && (
            <div className="bg-green-50 border border-green-200 text-green-700 px-4 py-3 rounded mb-4 flex items-start">
              <Check className="h-5 w-5 text-green-500 mr-2 mt-0.5 flex-shrink-0" />
              <div>Cập nhật ảnh thành công! Tất cả ảnh đã được chuyển sang Supabase Storage.</div>
            </div>
          )}
          
          <p className="text-gray-600 mb-4">
            Công cụ này sẽ quét tất cả bài viết và tài liệu, tìm các ảnh có URL từ vmon.vn, 
            tải lên Supabase Storage và cập nhật URL trong nội dung.
          </p>
          
          {isProcessing ? (
            <div className="space-y-4">
              <div className="flex items-center mb-2">
                <RefreshCw className="h-5 w-5 text-blue-600 animate-spin mr-2" />
                <span className="font-medium text-gray-900">Đang xử lý: {currentItem}</span>
              </div>
              
              <div className="w-full bg-gray-200 rounded-full h-2.5 mb-2">
                <div 
                  className="bg-blue-600 h-2.5 rounded-full transition-all duration-300" 
                  style={{ width: `${progress}%` }}
                ></div>
              </div>
              
              <div className="text-center text-sm text-gray-600">
                {processedCount} / {totalCount} ({Math.round(progress)}%)
              </div>
            </div>
          ) : (
            <button
              onClick={processAllContent}
              className="w-full py-3 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors"
            >
              Bắt đầu cập nhật
            </button>
          )}
        </div>

        {/* Logs */}
        {logs.length > 0 && (
          <div className="bg-white rounded-lg shadow p-6">
            <h2 className="text-xl font-bold text-gray-900 mb-4">Logs</h2>
            <div className="max-h-96 overflow-y-auto bg-gray-50 p-4 rounded-lg text-sm font-mono">
              {logs.map((log, index) => (
                <div key={index} className={`mb-1 ${
                  log.includes('❌') ? 'text-red-600' : 
                  log.includes('✅') ? 'text-green-600' : 
                  log.includes('🎉') ? 'text-blue-600 font-bold' : 
                  log.includes('ℹ️') ? 'text-gray-500' : 
                  'text-gray-700'
                }`}>
                  {log}
                </div>
              ))}
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default ContentBatchUpdater;